export type BillAnalysisSnapshot = {
  status: string;
  updated_at: string | null;
  error_message: string | null;
  progress_label: string;
};

function analysisTimestamp(analysis: BillAnalysisSnapshot): number {
  if (!analysis.updated_at) return 0;
  const time = new Date(analysis.updated_at).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function isRestartedProcessing(prop: BillAnalysisSnapshot, cached: BillAnalysisSnapshot): boolean {
  if (prop.status !== "processing") return false;
  return analysisTimestamp(prop) >= analysisTimestamp(cached);
}

/** Prefer fresher Inertia props over stale poll cache (e.g. after force re-precompute redirect). */
export function mergeBillAnalysis(
  prop: BillAnalysisSnapshot,
  cached?: BillAnalysisSnapshot
): BillAnalysisSnapshot {
  if (!cached) return prop;
  if (isRestartedProcessing(prop, cached)) return prop;

  const propTime = analysisTimestamp(prop);
  const cachedTime = analysisTimestamp(cached);
  if (propTime !== cachedTime) {
    return propTime > cachedTime ? prop : cached;
  }

  return cached;
}

const STALE_PROCESSING_MS = 30 * 60 * 1000;

function isStaleProcessing(status: string | null, updatedAt: string | null): boolean {
  if (status !== "processing" || !updatedAt) return false;

  const updatedMs = new Date(updatedAt).getTime();
  if (Number.isNaN(updatedMs)) return false;

  return Date.now() - updatedMs >= STALE_PROCESSING_MS;
}

/** True while Ops should poll/disable Precompute for this bill. */
export function isActiveAnalysis(
  status: string | null,
  watched: boolean,
  updatedAt: string | null = null
): boolean {
  if (status === "processing") return !isStaleProcessing(status, updatedAt);
  if (!watched) return false;
  return true;
}

/** Clear watchIds once a watched analysis reaches a terminal, absent, or stale status. */
export function shouldUnwatchAnalysis(
  status: string | null,
  watched: boolean,
  updatedAt: string | null = null
): boolean {
  if (!watched) return false;
  if (status === "processing" && isStaleProcessing(status, updatedAt)) return true;
  return !status || status === "completed" || status === "failed" || status === "superseded" || status === "none";
}

/** Watched bill IDs the poll response did not include (stale watch after failed enqueue). */
export function watchIdsMissingFromPoll(watchIds: number[], polledBillIds: Set<number>): number[] {
  return watchIds.filter((id) => !polledBillIds.has(id));
}

export const WATCH_STORAGE_KEY = "legislation-monitor-watch-ids";

export function readWatchIds(): number[] {
  try {
    const raw = sessionStorage.getItem(WATCH_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === "number") : [];
  } catch {
    return [];
  }
}

export function writeWatchIds(ids: number[]) {
  sessionStorage.setItem(WATCH_STORAGE_KEY, JSON.stringify(ids));
}

export function addWatchId(billId: number): number[] {
  const next = [...new Set([...readWatchIds(), billId])];
  writeWatchIds(next);
  return next;
}

export function removeWatchId(billId: number): number[] {
  const next = readWatchIds().filter((id) => id !== billId);
  writeWatchIds(next);
  return next;
}

export function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "completed":
      return "default";
    case "processing":
      return "secondary";
    case "failed":
      return "destructive";
    case "superseded":
      return "outline";
    case "pending":
    case "none":
      return "outline";
    default:
      return "outline";
  }
}
